import { HttpStatusCode } from "axios";
import { i18n, TFunction } from "i18next";
import { startCase } from "lodash";

import {
  errors,
  METADATA_KEY,
  PLURAL_KEY,
  SINGLE_KEY,
  TRANSLATION_KEY,
  TRANSLATION_PARAMS_KEY,
} from "../constants";
import { ResponseStatusEnum } from "../enums";
import {
  isLabelItem,
  isTranslatableItem,
  Log,
  translateInfoItem,
} from "../helpers";
import {
  ITranslatableItem,
  RydeUiUnit,
  TError,
  TTranslationParams,
} from "./common.models";

export type TApiSuccess<T> = {
  status: ResponseStatusEnum.Success;
  data?: T;
};

export type TApiResponse<T> = TApiSuccess<T> | IApiError;

export interface IAxiosValidationError {
  message: string;
  errors: Record<string, string[]>;
}

export interface IValidationError {
  path?: string;
  name: string;
  message: string;
  [TRANSLATION_KEY]: string;
  [TRANSLATION_PARAMS_KEY]?: TTranslationParams;
}

/** Item in `IApiError.validationErrors` from core API (yup `serializeValidationError`). */
export interface IApiSerializedValidationError {
  path?: string;
  name?: string;
  message: string;
  translationKey?: string;
  [TRANSLATION_PARAMS_KEY]?: TTranslationParams;
}

export interface IApiErrorMetadata {
  [key: string]: unknown;
  token: string;
  errors: string[];
}

export interface IApiError extends Partial<ITranslatableItem> {
  status: ResponseStatusEnum.Error;
  errors?: (string | IValidationError)[];
  message?: string;
  cause?: unknown;
  validationErrors?: IApiSerializedValidationError[];
  metadata?: IApiErrorMetadata;
  traceId?: string;
  debugInfo?: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isValidationError = (err: any): err is IValidationError => {
  return !!err?.message;
};

const normalizeMessage = (errMessage: string | null | undefined): string => {
  if (!errMessage) {
    return "";
  }

  return errMessage.replace(`${METADATA_KEY}.`, "");
};

const transformError = (err: string | IValidationError): string => {
  if (isValidationError(err)) {
    return normalizeMessage(err.message);
  }

  return normalizeMessage(err);
};

export const normalizeErrors = (
  err: IApiError | TError | null | undefined,
  translationFunction: TFunction<"translation", undefined>,
): string[] => {
  if (!err) {
    return [translationFunction(errors.common.technicalProblemOccurred)];
  }

  if (typeof err === "string") {
    return [err];
  }

  if (Array.isArray(err)) {
    return err.flatMap(x => normalizeErrors(x, translationFunction));
  }

  const result: string[] = [];
  const apiError = err as IApiError;

  if (apiError?.validationErrors?.length) {
    result.push(
      ...(apiError.validationErrors
        .map(x => x.message)
        .filter(Boolean) as string[]),
    );
  }

  if (apiError?.errors?.length) {
    result.push(...(apiError?.errors?.map(x => transformError(x)) || []));
  }

  if (isTranslatableItem(err)) {
    return [translateInfoItem(err, translationFunction), ...result];
  }

  if (isLabelItem(err)) {
    return [err.label, ...result];
  }

  if (result.length) {
    return result;
  }

  if (err instanceof Error) {
    if (err.message) {
      return [err.message];
    }

    return [err.name];
  }

  return [
    err?.message || translationFunction(errors.common.technicalProblemOccurred),
  ];
};

export const normalizeReferencedEntitiesErrors = (
  err: IApiError | TError | null | undefined,
  entity: RydeUiUnit,
  i18nInstance: i18n,
  translationFunction: TFunction<"translation", undefined>,
): string[] => {
  const errorsObject = err;

  if (
    isTranslatableItem(errorsObject) &&
    errorsObject.translationKey === errors.common.archiveRelationError
  ) {
    const parts: string[] = [];

    for (const [key, value] of Object.entries(
      errorsObject.translationParams ?? {},
    )) {
      const entityTranslatedKey = `${key}.${PLURAL_KEY}`;
      const entityTranslatedVal = i18nInstance.exists(entityTranslatedKey)
        ? startCase(translationFunction(entityTranslatedKey))
        : key;

      if (entityTranslatedVal) {
        parts.push(`${entityTranslatedVal}: ${value}`);
      }
    }

    const relatedEntitiesTranslatedMsg = parts.join(", ");

    if (relatedEntitiesTranslatedMsg.length) {
      errorsObject.translationParams = {
        relatedEntitiesTranslatedMsg,
        entity: translationFunction(`${entity}.${SINGLE_KEY}`),
      };
    }
  }

  return normalizeErrors(errorsObject, translationFunction);
};

export class ApiException extends Error implements IApiError {
  errors?: (string | IValidationError)[];
  status: ResponseStatusEnum.Error;
  statusCode: HttpStatusCode | undefined;
  metadata: IApiErrorMetadata | undefined;
  translationKey: string | undefined;
  translationParams?: TTranslationParams | undefined;
  validationErrors?: IApiSerializedValidationError[] | undefined;
  debugInfo?: unknown;
  constructor(err: IApiError, statusCode?: HttpStatusCode) {
    super(err.message, { cause: err.cause });

    Log[statusCode !== HttpStatusCode.Conflict ? "error" : "debug"](
      `Api exception${err.traceId ? `, traceId: ${err.traceId}` : ""}`,
      err,
    );

    this.message = err.message || "";
    this.translationKey = err.translationKey;
    this.translationParams = err.translationParams;
    this.errors = err.errors;
    this.status = ResponseStatusEnum.Error;
    this.statusCode = statusCode;
    this.metadata = err.metadata;
    this.validationErrors = err.validationErrors;
    this.debugInfo = err.debugInfo;
  }
}
